export const BOIL_HZ = 12;
export const BOIL_AMP = 0.01;
export const HEAVY_ANGLE = Math.PI / 4;
export const RING_SEGMENTS = 40;
export const LIGHT_ANGLE = (-3 * Math.PI) / 4;

const TAU = Math.PI * 2;

export function hash01(a: number, b: number, c = 0): number {
  let h = Math.imul(a | 0, 0x9e3779b1) ^ Math.imul((b | 0) + 0x632be5ab, 0x85ebca77) ^ Math.imul((c | 0) + 0x1b873593, 0xc2b2ae3d);
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d);
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

export function seedOf(text: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function boilFrame(time: number, reduced: boolean): number {
  if (reduced || !Number.isFinite(time)) return 0;
  return Math.floor(time * BOIL_HZ);
}

function wave(theta: number, p1: number, p2: number): number {
  return 0.6 * Math.sin(2 * theta + p1) + 0.4 * Math.sin(3 * theta + p2);
}

export function boilAt(seed: number, frame: number, theta: number): number {
  return wave(theta, hash01(seed, frame, 1) * TAU, hash01(seed, frame, 2) * TAU);
}

export function inkWidth(theta: number, base: number): number {
  return base * (0.7 + 0.6 * (0.5 + 0.5 * Math.cos(theta - HEAVY_ANGLE)));
}

export function ringPath(
  outer: number[],
  inner: number[],
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  base: number,
  seed: number,
  frame: number,
  amp: number,
  segments = RING_SEGMENTS,
): void {
  outer.length = 0;
  inner.length = 0;
  const p1 = hash01(seed, frame, 1) * TAU;
  const p2 = hash01(seed, frame, 2) * TAU;
  for (let i = 0; i < segments; i++) {
    const theta = (i / segments) * TAU;
    const k = 1 + (amp === 0 ? 0 : amp * wave(theta, p1, p2));
    const half = inkWidth(theta, base) / 2;
    const c = Math.cos(theta);
    const s = Math.sin(theta);
    outer.push(cx + c * (rx * k + half), cy + s * (ry * k + half));
    inner.push(cx + c * Math.max(0, rx * k - half), cy + s * Math.max(0, ry * k - half));
  }
}

export function litOffset(radius: number, depth: number, angle = LIGHT_ANGLE): { dx: number; dy: number; r: number } {
  const d = Math.max(0, Math.min(1, depth));
  return { dx: Math.cos(angle) * d * radius, dy: Math.sin(angle) * d * radius, r: radius * (1 - d) };
}

export function hatchChords(
  out: number[],
  radius: number,
  angle: number,
  spacing: number,
  lx: number,
  ly: number,
  lr: number,
): number {
  out.length = 0;
  if (!(radius > 0) || !(spacing > 0)) return 0;
  const ux = Math.cos(angle);
  const uy = Math.sin(angle);
  const nx = -uy;
  const ny = ux;
  const qu = lx * ux + ly * uy;
  const qn = lx * nx + ly * ny;
  let count = 0;
  const push = (d: number, t0: number, t1: number): void => {
    if (t1 - t0 <= spacing * 0.25) return;
    out.push(d * nx + t0 * ux, d * ny + t0 * uy, d * nx + t1 * ux, d * ny + t1 * uy);
    count += 1;
  };
  for (let d = -radius + spacing / 2; d < radius; d += spacing) {
    const h = Math.sqrt(radius * radius - d * d);
    const off = d - qn;
    if (lr > 0 && Math.abs(off) < lr) {
      const half = Math.sqrt(lr * lr - off * off);
      const u0 = Math.max(-h, qu - half);
      const u1 = Math.min(h, qu + half);
      if (u0 >= u1) push(d, -h, h);
      else {
        push(d, -h, u0);
        push(d, u1, h);
      }
    } else push(d, -h, h);
  }
  return count;
}

export function sag(x0: number, y0: number, x1: number, y1: number, depth: number): { x: number; y: number } {
  const span = Math.hypot(x1 - x0, y1 - y0);
  return { x: (x0 + x1) / 2, y: (y0 + y1) / 2 + span * depth };
}
